"use client";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import useMobileDetect from "@/hooks/useMobileDetect";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { ProductInterface } from "@/service/search/interface";
import { handleImgUrl, isObjectEmpty } from "@/utils/tools";
import type { Swiper as SwiperType } from "swiper"; // 导入 Swiper 类型

import "swiper/css";
// import "swiper/css/navigation";
import "swiper/css/effect-fade";
import { ProductItemCard } from "../../common/ProductItemCard";

type ProductListCarouselProps = {
  isFirstMobile?: boolean;
  list: any[];
  title: string;
  viewAllUrl: string;
  lineSpuCount: number;
  showSkuItems?:boolean;
  showExperienceScore?:boolean;
};
export const ProductListCarousel: React.FC<ProductListCarouselProps> = ({
  isFirstMobile,
  list,
  title,
  viewAllUrl,
  lineSpuCount,
  showSkuItems,
  showExperienceScore
}) => {
  const isMobile = useMobileDetect(isFirstMobile);
  const swiperRef = useRef<SwiperType | null>(null);
  const [allowSlidePrev, setAllowSlidePrev] = useState(false);
  const [allowSlideNext, setAllowSlideNext] = useState(true);
  return (
    <div className="mx-20 md:mx-40">
      <div
        className={`flex items-center justify-between text-bravve-100 ${isMobile ? "mb-24" : "mb-48"} `}
      >
        <h2
          className={`leading-32 ${isMobile ? "text-[22px]" : "text-4.2xl"} font-bold`}
        >
          {title}
        </h2>

        <div className="relative flex items-center justify-between gap-x-16">
          {
            viewAllUrl ?
            <h3 className={`w-60`}>
              <Link
                href={viewAllUrl}
                target="_blank"
                onClick={(e) => {
                  // @ts-ignore
                  const userInfoStr =
                    globalThis?.localStorage?.getItem("userInfo");
                  let userInfo;
                  if (userInfoStr) {
                    userInfo = JSON.parse(userInfoStr);
                  }
                  const cardItemDom = (e.target as HTMLElement).closest(
                    ".cardItem",
                  ) as HTMLElement | null;
                  if (cardItemDom && cardItemDom.dataset) {
                    const { card_type, card_position } = cardItemDom.dataset;
                    // @ts-ignore
                    window.gtag("event", "card_click", {
                      card_type,
                      card_position,
                      click_time: Date(),
                      click_area: "View All",
                      target_url: viewAllUrl,
                      user_id: userInfo?.id,
                    });
                  }
                }}
                className="text-lg underline"
              >
                View All
              </Link>
            </h3>
            :
            null
          }
          { isMobile || lineSpuCount >= list.length ? null : (
            <div className={"flex items-center justify-between"}>
              <iconpark-icon
                name="left"
                color="#402C22"
                size="24"
                class={(allowSlidePrev ? "" : "opacity-30") + " cursor-pointer"}
                icon-id="left"
                onClick={() => {
                  swiperRef.current?.slidePrev();
                }}
              />
              <iconpark-icon
                name="right"
                color="#402C22"
                size="24"
                class={(allowSlideNext ? "" : "opacity-30") + " cursor-pointer"}
                icon-id="right"
                onClick={() => {
                  swiperRef.current?.slideNext();
                }}
              />
            </div>
          )}
        </div>
      </div>
      <div className="mx-[-8px]">
        {!isMobile ? (
          <Swiper
            onSwiper={(swiper) => {
              swiperRef.current = swiper; // 存储 Swiper 实例
            }}
            onSlideChange={(swiper: SwiperType) => {
              // console.log('活动幻灯片已更改，当前索引:', swiper.activeIndex);
              if (swiperRef.current?.isEnd !== undefined) {
                setAllowSlideNext(!swiperRef.current?.isEnd);
              }
              if (swiperRef.current?.isBeginning !== undefined) {
                setAllowSlidePrev(!swiperRef.current?.isBeginning);
              }
            }}
            className={`ProductListCarousel item-w-${lineSpuCount}`}
            // modules={[Navigation]}
            slidesPerView={lineSpuCount}
            slidesPerGroup={lineSpuCount}
            // navigation={true}
          >
            {list.map(
              (
                {
                  spuPicList,
                  productName,
                  salePrice,
                  originPrice,
                  detailUrl,
                  rating,
                  category,
                  spuCode,
                  productItems,
                  id,
                  newProduct,
                }: ProductInterface,
                idx: number,
              ) => {
                return (
                  <SwiperSlide key={detailUrl}>
                    <ProductItemCard
                      cardType="PRODUCT_COLLECTION"
                      idx={idx}
                      spuId={id} 
                      category={category}
                      spuCode={spuCode}
                      productItems={productItems}
                      linkUrl={detailUrl ?? ""}
                      img={
                        spuPicList
                          ? 
                          spuPicList.map((item) => handleImgUrl(item, "616x616"))
                          : 
                          []
                      }
                      product={productName}
                      container={`w-full cardItemContent`}
                      ratingNum={parseFloat(rating ?? "0")}
                      currentPrice={salePrice}
                      originalPrice={originPrice}
                      isIcon={false}
                      newProduct={newProduct}
                      showSkuItems={showSkuItems}
                      showExperienceScore={showExperienceScore}
                    />
                  </SwiperSlide>
                );
              },
            )}
          </Swiper>
        ) : (
          <div className="flex items-stretch justify-between overflow-x-auto">
            {list.map(
              (
                {
                  spuPicList,
                  productName,
                  salePrice,
                  originPrice,
                  detailUrl,
                  rating,
                  spuCode,
                  category,
                  productItems,
                  id,
                  newProduct,
                }: ProductInterface,
                idx: number,
              ) => {
                return (
                  <ProductItemCard
                    idx={idx}
                    spuId={id}
                    cardType="PRODUCT_COLLECTION"
                    spuCode={spuCode}
                    category={category}
                    productItems={productItems}
                    key={detailUrl}
                    linkUrl={detailUrl ?? ""}
                    img={
                      spuPicList
                        ? spuPicList.map((item) =>
                            handleImgUrl(item, "616x616"),
                          )
                        : []
                    }
                    product={productName}
                    container={
                      isMobile ? (lineSpuCount > 4 ? "w-4/5 cardItemContent" : "w-4/5 cardItemContent") : "cardItemContent"
                    }
                    ratingNum={parseFloat(rating ?? "0")}
                    currentPrice={salePrice}
                    originalPrice={originPrice}
                    isIcon={false}
                    newProduct={newProduct}
                    showSkuItems={showSkuItems}
                    showExperienceScore={showExperienceScore}
                  />
                );
              },
            )}
          </div>
        )}
      </div>
    </div>
  );
};
